import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './ModelTraining.css';

function ModelTraining({ sessionId, selectedColumns, columnMappings, hyperparameters, onTrainingComplete, onBack }) {
  const [training, setTraining] = useState(false);
  const [error, setError] = useState(null);
  const [trainingResults, setTrainingResults] = useState(null);
  const hasTrainedRef = useRef(false);

  useEffect(() => {
    if (!hasTrainedRef.current) {
      hasTrainedRef.current = true;
      trainModel();
    }
  }, []);

  const trainModel = async () => {
    setTraining(true);
    setError(null);

    try {
      const response = await axios.post('/api/train', {
        session_id: sessionId,
        column_mappings: columnMappings,
        selected_columns: selectedColumns,
        hyperparameters: hyperparameters
      });

      setTrainingResults(response.data);
      setTraining(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to train model');
      setTraining(false);
    }
  };

  const handleNext = () => {
    onTrainingComplete(trainingResults);
  };

  return (
    <div className="model-training">
      {training && (
        <div className="training-progress">
          <div className="progress-step">
            <div className="spinner"></div>
            <div className="progress-info">
              <h3>Training Random Forest Classifier</h3>
              <p>Training model with {selectedColumns.length} features...</p>
            </div>
          </div>
        </div>
      )}

      {trainingResults && (
        <div className="training-results">
          <div className="success">
            ✓ Model trained successfully!
          </div>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">{(trainingResults.train_accuracy * 100).toFixed(1)}%</div>
              <div className="metric-label">Training Accuracy</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{(trainingResults.test_accuracy * 100).toFixed(1)}%</div>
              <div className="metric-label">Test Accuracy</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{trainingResults.best_threshold.toFixed(3)}</div>
              <div className="metric-label">Classification Threshold</div>
            </div>
          </div>

          <div className="threshold-explanation" style={{ 
            marginTop: '30px', 
            padding: '20px', 
            backgroundColor: 'rgba(102, 126, 234, 0.1)', 
            borderRadius: '8px',
            borderLeft: '4px solid #667eea'
          }}>
            <h3 style={{ marginTop: 0, color: '#a78bfa', fontSize: '1.1rem' }}>About the Classification Threshold</h3>
            <p style={{ margin: '10px 0', lineHeight: '1.6', color: 'rgba(224, 224, 255, 0.85)' }}>
              The classification threshold determines when a galaxy is labeled as backsplash versus infalling. 
              The model outputs a probability for each galaxy, and this threshold is the cutoff point for making the final decision.
            </p>
            <p style={{ margin: '10px 0', lineHeight: '1.6', color: 'rgba(224, 224, 255, 0.85)' }}>
              The threshold shown above ({trainingResults.best_threshold.toFixed(3)}) was chosen to get the most accurate 
              fractions of backsplash galaxies in each cluster of the training data. This approach helps handle the imbalanced 
              nature of the dataset. However, you can choose your own threshold based on your research needs. In the next step, 
              view diagnostic plots to see how different thresholds affect completeness and purity for each galaxy type. 
              Then in the final step, you'll get both classifications and probabilities for your data, which you can use with 
              any threshold you prefer.
            </p>
          </div>

          <div className="actions" style={{ marginTop: '30px' }}>
            <button className="button button-secondary" onClick={onBack}>
              ← Back to Column Selection
            </button>
            <button className="button" onClick={handleNext}>
              Next: View Diagnostic Plots →
            </button>
          </div>
        </div>
      )}

      {error && (
        <>
          <div className="error">{error}</div>
          <div className="actions">
            <button className="button button-secondary" onClick={onBack}>
              ← Back to Column Selection
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ModelTraining;
